import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ArrowRight, Bookmark, Loader2, Sparkles, CheckCircle2, Clock, Circle } from "lucide-react";
import { toast } from "sonner";
import PDFCTABanner from "../components/PDFCTABanner";

const STATUS_META = {
  not_started: { label: "Not started", Icon: Circle, color: "text-[#5C5C55] bg-white border-[#E5E5E0]" },
  in_progress: { label: "In progress", Icon: Clock, color: "text-[#0A0A0A] bg-[#FACC15] border-[#FACC15]" },
  completed: { label: "Completed", Icon: CheckCircle2, color: "text-white bg-[#166534] border-[#166534]" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [careers, setCareers] = useState([]);
  const [coursesProgress, setCoursesProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [careersRes, coursesRes] = await Promise.all([
          api.get("/me/careers"),
          api.get("/me/courses"),
        ]);
        setCareers(careersRes.data.items);
        setCoursesProgress(coursesRes.data.items);
      } catch {
        toast.error("Could not load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  const stats = {
    saved: careers.length,
    completed: coursesProgress.filter((c) => c.status === "completed").length,
    in_progress: coursesProgress.filter((c) => c.status === "in_progress").length,
  };

  return (
    <main className="min-h-screen bg-[#F4F4F0]" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-2">Control room</div>
            <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter leading-none">
              Hi, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="mt-3 text-[#5C5C55] max-w-xl">
              Your saved careers and learning progress, all in one place.
            </p>
          </div>
          <Link to="/quiz" data-testid="dashboard-take-quiz" className="btn-primary">
            <Sparkles className="w-4 h-4" /> Retake quiz
          </Link>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0] rounded-sm overflow-hidden mb-8">
          <StatCell label="Saved careers" value={stats.saved} testid="stat-saved" />
          <StatCell label="Courses in progress" value={stats.in_progress} testid="stat-progress" />
          <StatCell label="Completed" value={stats.completed} testid="stat-completed" />
        </div>

        <div className="mb-12">
          <PDFCTABanner variant="full" />
        </div>

        {/* Saved careers */}
        <section className="mb-14" data-testid="saved-careers-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-3xl font-bold tracking-tight">Saved careers</h2>
            <span className="overline">{careers.length} total</span>
          </div>

          {careers.length === 0 ? (
            <div className="border border-dashed border-[#E5E5E0] bg-white p-12 text-center rounded-sm">
              <Bookmark className="w-8 h-8 text-[#5C5C55] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#5C5C55] mb-4">You haven&apos;t saved any careers yet.</p>
              <Link to="/quiz" data-testid="empty-take-quiz" className="btn-primary inline-flex">
                Take the quiz <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {careers.map((item, idx) => (
                <Link
                  key={item.career.career_id}
                  to={`/careers/${item.career.career_id}`}
                  data-testid={`saved-career-${idx}`}
                  className="bg-white border border-[#E5E5E0] rounded-sm p-6 hover:border-[#002FA7]/40 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="font-mono text-xs text-[#5C5C55]">#{String(idx + 1).padStart(2, "0")}</span>
                    <span className="font-mono text-sm bg-[#002FA7]/10 text-[#002FA7] px-2 py-0.5 border border-[#002FA7]/20 rounded-sm">
                      {item.career.match_percentage}%
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold leading-tight mb-2">{item.career.title}</h3>
                  <p className="text-sm text-[#5C5C55] line-clamp-3">{item.career.description}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs font-mono text-[#5C5C55]">{item.career.salary_range}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Course tracker */}
        <section data-testid="course-tracker-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-3xl font-bold tracking-tight">Course tracker</h2>
            <span className="overline">{coursesProgress.length} tracked</span>
          </div>

          {coursesProgress.length === 0 ? (
            <div className="border border-dashed border-[#E5E5E0] bg-white p-12 text-center rounded-sm">
              <Clock className="w-8 h-8 text-[#5C5C55] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#5C5C55]">Mark courses as started or completed to see them here.</p>
            </div>
          ) : (
            <div className="bg-white border border-[#E5E5E0] rounded-sm overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-[#F4F4F0] border-b border-[#E5E5E0] text-xs font-mono uppercase tracking-wider text-[#5C5C55]">
                <div className="col-span-6">Course</div>
                <div className="col-span-3 hidden md:block">Career</div>
                <div className="col-span-3 md:col-span-3">Status</div>
              </div>
              {coursesProgress.map((p, idx) => {
                const meta = STATUS_META[p.status] || STATUS_META.not_started;
                return (
                  <div
                    key={p.course_id}
                    data-testid={`tracked-course-${idx}`}
                    className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#E5E5E0] last:border-b-0 items-center"
                  >
                    <div className="col-span-9 md:col-span-6">
                      <div className="font-semibold">{p.course?.title || "Untitled course"}</div>
                      <div className="text-xs text-[#5C5C55] font-mono mt-0.5">{p.course?.provider}</div>
                    </div>
                    <div className="col-span-3 hidden md:block text-sm">
                      {p.career_id ? (
                        <Link to={`/careers/${p.career_id}`} className="hover:text-[#002FA7] transition-colors">
                          {p.career_title || "—"}
                        </Link>
                      ) : (
                        <span className="text-[#5C5C55]">—</span>
                      )}
                    </div>
                    <div className="col-span-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-2.5 py-1.5 border rounded-sm ${meta.color}`}>
                        <meta.Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function StatCell({ label, value, testid }) {
  return (
    <div className="bg-white p-6 md:p-8" data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      <div className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-[#0A0A0A]">{value}</div>
    </div>
  );
}
